import React from 'react';
import { logAppCrash } from '../services/analytics';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log the error to Firebase Analytics
        console.error('🔴 App Crash:', error, errorInfo);
        logAppCrash(error, errorInfo);

        // Update state with error details
        this.setState({
            error: error,
            errorInfo: errorInfo
        });
    }

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            // Fallback UI when error occurs
            return (
                <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
                    <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8 border-2 border-red-200">
                        <div className="text-center mb-6">
                            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">Oops! Something Went Wrong</h1>
                            <p className="text-gray-600 mb-6">
                                We're sorry, but the application encountered an unexpected error.
                            </p>
                        </div>

                        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg mb-6">
                            <p className="text-sm text-red-800 font-semibold mb-2">Error Details:</p>
                            <p className="text-sm text-red-700 font-mono break-all">
                                {this.state.error?.message || 'Unknown error'}
                            </p>
                        </div>

                        <div className="space-y-3">
                            <button
                                onClick={this.handleReload}
                                className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                Reload Application
                            </button>

                            <button
                                onClick={() => window.location.href = '/'}
                                className="w-full px-6 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all duration-200"
                            >
                                Go to Homepage
                            </button>
                        </div>

                        <div className="mt-6 text-center">
                            <p className="text-sm text-gray-500">
                                This error has been automatically reported to our team.
                            </p>
                            <p className="text-sm text-gray-500 mt-2">
                                Contact: <a href="mailto:hanansani8@gmail.com" className="text-indigo-600 hover:underline">hanansani8@gmail.com</a>
                            </p>
                        </div>

                        {/* Show detailed error in development */}
                        {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
                            <details className="mt-6 bg-gray-50 rounded-lg p-4">
                                <summary className="cursor-pointer text-sm font-semibold text-gray-700 mb-2">
                                    Developer Info (Development Only)
                                </summary>
                                <pre className="text-xs text-gray-600 overflow-auto max-h-40">
                                    {this.state.errorInfo.componentStack}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
